// src/pages/UserManagement/index.tsx
import React, { useEffect } from 'react';
import './UserManagement.scss';
import { Container, Row, Col, Card, CardBody, CardHeader, Button } from 'reactstrap';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

// Components
import BreadCrumb from 'Components/Common/BreadCrumb';
import UserList from './UserList';
import UserFilters from './UserFilters';
import UserPagination from './UserPagination';
import CreateUserModal from './modals/CreateUserModal';
import EditUserModal from './modals/EditUserModal';
import DeleteUserDialog from './modals/DeleteUserDialog';
import AvatarUploadModal from './modals/AvatarUploadModal';
import PermissionsModal from './modals/PermissionsModal';

// Redux
import {
  openModal,
  closeModal,
  setSelectedUser,
} from 'slices/userManagement/reducer';
import {
  fetchUsers,
  createNewUser,
  updateExistingUser,
  deleteUserById,
  toggleStatus,
} from 'slices/userManagement/thunk';

// API helpers
import { getTenants } from 'helpers/fakebackend_helper';
import { getRoles } from 'helpers/fakebackend_helper';

// Types
import { User } from './types';

// Hooks
import { useBrandingTitle } from '../../hooks';
import { useScreenText } from 'helpers/screenText';
import { createScreenText } from 'helpers/screenText';
const tx = createScreenText('user_management');

const UserManagement: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const st = useScreenText('user_management');
  
  // Local state for tenants and roles
  const [tenants, setTenants] = React.useState<any[]>([]);
  const [roles, setRoles] = React.useState<any[]>([]);
  const [loadingDropdowns, setLoadingDropdowns] = React.useState(false);
  
  // Redux state
  const {
    users,
    selectedUser,
    loading,
    pagination,
    filters,
    sortBy,
    sortOrder,
    modalState,
  } = useSelector((state: any) => state.UserManagement);

  // Load tenants and roles on mount
  useEffect(() => {
    fetchDropdownData();
  }, []);

  // Load users from API when filters/pagination/sorting change
  useEffect(() => {
    loadUsers();
  }, [filters, sortBy, sortOrder, pagination.currentPage, pagination.pageSize]);

  // Lỗi đã được toast trực tiếp trong từng handler (handleCreateSubmit, handleEditSubmit,
  // handleConfirmDelete, handleToggleStatus) ngay tại nơi catch — KHÔNG toast lại ở đây theo
  // Redux state.error, vì các *.rejected reducer cũng set state.error cho message tương tự, dẫn
  // tới hiển thị 2 toast trùng nội dung cho cùng 1 lỗi.
      useBrandingTitle(tx('quan_tri_tai_khoan_text'));

  // Fetch dropdown data (tenants and roles)
  const fetchDropdownData = async () => {
    try {
      setLoadingDropdowns(true);
      
      // Fetch tenants and roles in parallel
      const [tenantsResponse, rolesResponse] = await Promise.all([
        getTenants({ pageSize: 1000 }), // Get all tenants
        getRoles({ pageSize: 1000 }), // Get all roles
      ]);
      
      // Unwrap responses
      const tenantsData = tenantsResponse.data || tenantsResponse;
      const rolesData = rolesResponse.data || rolesResponse;
      
      // Set tenants - handle both array and paginated response
      const tenantList = Array.isArray(tenantsData) 
        ? tenantsData 
        : (tenantsData.items || tenantsData.data || []);
      setTenants(tenantList);
      
      // Set roles - handle both array and paginated response
      const roleList = Array.isArray(rolesData)
        ? rolesData
        : (rolesData.items || rolesData.data || []);
      setRoles(roleList);
      
    } catch (error: any) {
      console.error('Error fetching dropdown data:', error);
      toast.error(tx('khong_the_tai_danh_sach_don_message'));
      setTenants([]);
      setRoles([]);
    } finally {
      setLoadingDropdowns(false);
    }
  };
  
  const loadUsers = () => {
    // Build API params
    const params: any = {
      PageIndex: pagination.currentPage,
      PageSize: pagination.pageSize,
      search: filters.searchTerm || undefined,
      departmentId: filters.departmentId,
      roleId: filters.roleId,
      status: filters.status !== 'All' ? filters.status : undefined,
      sortBy,
      sortOrder,
    };
    
    // Remove undefined values
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value !== undefined)
    );
    
    // Fetch users from API
    dispatch(fetchUsers(cleanParams));
  };
  
  // Handlers
  const handleCreateUser = () => {
    dispatch(openModal('create'));
  };
  
  const handleCreateSubmit = async (values: any) => {
    try {
      await dispatch(createNewUser(values)).unwrap();
      toast.success(st('toast_create_success'));
      dispatch(closeModal('create'));
      loadUsers();
    } catch (error: any) {
      toast.error(error || st('toast_create_failed'));
    }
  };
  
  const handleEditUser = (user: User) => {
    dispatch(setSelectedUser(user));
    dispatch(openModal('edit'));
  };
  
  const handleEditSubmit = async (values: any) => {
    try {
      await dispatch(updateExistingUser(values)).unwrap();
      toast.success(tx('cap_nhat_nguoi_dung_thanh_cong_message'));
      dispatch(closeModal('edit'));
      dispatch(setSelectedUser(null));
      loadUsers();
    } catch (error: any) {
      toast.error(error || tx('co_loi_xay_ra_khi_cap_message'));
    }
  };
  
  const handleDeleteUser = (user: User) => {
    dispatch(setSelectedUser(user));
    dispatch(openModal('delete'));
  };
  
  const handleConfirmDelete = async () => {
    if (selectedUser) {
      try {
        await dispatch(deleteUserById(selectedUser.id)).unwrap();
        toast.success(tx('xoa_nguoi_dung_thanh_cong_message'));
        dispatch(closeModal('delete'));
        dispatch(setSelectedUser(null));
        // Reload users list
        loadUsers();
      } catch (error: any) {
        toast.error(error || tx('co_loi_xay_ra_khi_xoa_message'));
      }
    }
  };
  
  const handleToggleStatus = async (user: User) => {
    const newStatus = user.status === 'Active' ? 'Locked' : 'Active';
    try {
      await dispatch(toggleStatus({ id: user.id, status: newStatus })).unwrap();
      toast.success(tx('da_value_tai_khoan_message', { value: newStatus === 'Active' ? tx('mo_khoa_message') : tx('khoa_message') }));
      loadUsers();
    } catch (error: any) {
      toast.error(error || tx('co_loi_xay_ra_khi_thay_message'));
    }
  };
  
  const handleUploadAvatar = (user: User) => {
    dispatch(setSelectedUser(user));
    dispatch(openModal('avatar'));
  };
  
  const handleUpdatePermissions = (user: User) => {
    dispatch(setSelectedUser(user));
    dispatch(openModal('permissions'));
  };
  
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {/* Breadcrumb */}
          <BreadCrumb title={tx('quan_ly_tai_khoan_title')} pageTitle="Trang" />
          
          <Row>
            <Col lg={12}>
                            <Card>
                <CardHeader>
                  {/* Filters với nút Thêm mới */}
                  <UserFilters 
                    departments={tenants}
                    roles={roles}
                    onCreateUser={handleCreateUser}
                  />
                </CardHeader>
                
                <CardBody>
                  
                  {/* User List Table */}
                  <UserList
                    users={users}
                    loading={loading}
                    tenants={tenants}
                    roles={roles}
                    onEdit={handleEditUser}
                    onDelete={handleDeleteUser}
                    onToggleStatus={handleToggleStatus}
                    onUploadAvatar={handleUploadAvatar}
                    onUpdatePermissions={handleUpdatePermissions}
                    page={pagination.currentPage}
                    pageSize={pagination.pageSize}
                  />
                  
                  {/* Pagination */}
                  <UserPagination />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      
      {/* Modals */}
      <CreateUserModal
        isOpen={modalState.create}
        toggle={() => dispatch(closeModal('create'))}
        departments={tenants}
        roles={roles}
        onSubmit={handleCreateSubmit}
      />

      <EditUserModal
        isOpen={modalState.edit}
        toggle={() => dispatch(closeModal('edit'))}
        user={selectedUser}
        departments={tenants}
        roles={roles}
        onSubmit={handleEditSubmit}
      />
      
      <DeleteUserDialog
        isOpen={modalState.delete}
        onClose={() => dispatch(closeModal('delete'))}
        onDelete={handleConfirmDelete}
        userName={selectedUser?.name || ''}
      />
      
      <AvatarUploadModal
        isOpen={modalState.avatar}
        toggle={() => dispatch(closeModal('avatar'))}
        user={selectedUser}
      />
      
      <PermissionsModal
        isOpen={modalState.permissions}
        toggle={() => dispatch(closeModal('permissions'))}
        user={selectedUser}
        roles={roles}
        onUpdated={loadUsers}
      />
    </React.Fragment>
  );
};

export default UserManagement;